import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, MapPin, BadgeCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { KineticLines, Reveal } from "@/components/KineticLines";
import { TextMarquee } from "@/components/Marquee";
import { Chapters } from "@/components/Chapters";
import { Gallery } from "@/components/Gallery";
import { QuoteForm } from "@/components/QuoteForm";
import { Footer } from "@/components/Footer";

const CHAPTERS = [
  {
    num: "01",
    title: "NEW SIDING INSTALLATION",
    text: "Vinyl, fiber cement, cedar shake and metal panels. We help you pick the right material for your home and budget, then install it to manufacturer spec so your warranty stays intact.",
    position: "38% center",
    image: "https://images.pexels.com/photos/7601153/pexels-photo-7601153.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
  {
    num: "02",
    title: "FULL SIDING REPLACEMENT",
    text: "Tear-off, inspection of the sheathing underneath, new house wrap and flashing, then brand new siding. Your home ends up tighter, quieter and worth more.",
    image: "https://images.unsplash.com/photo-1781920872332-e9ccdde6fd3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGRhcmslMjBzaWRpbmd8ZW58MHx8fHwxNzg2MzY4MzY4fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "03",
    title: "REPAIR & STORM DAMAGE",
    text: "Wind, hail, fallen branches — we match your existing siding, fix the damage properly, and document everything for your insurance claim.",
    image: "https://images.unsplash.com/photo-1774177873629-4174653c2f5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGRhcmslMjBzaWRpbmd8ZW58MHx8fHwxNzg2MzY4MzY4fDA&ixlib=rb-4.1.0&q=85",
  },
];

const STATS = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: MapPin, label: "Serving NY · CT · MA" },
  { icon: BadgeCheck, label: "Free On-Site Estimates" },
];

export default function Residential() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
  };

  return (
    <div data-testid="residential-page" className="bg-background relative">
      <Header />

      <section ref={heroRef} data-testid="residential-hero" className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7601153/pexels-photo-7601153.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85"
            alt="Home with new siding"
            className="w-full h-full object-cover scale-110 object-[38%_center] saturate-[1.15]"
          />
        </motion.div>
        <div className="absolute inset-0 bg-[#131510]/40" />
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#3f7a2a]/40 via-[#3f7a2a]/15 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-3/4 h-1/2 bg-[radial-gradient(ellipse_at_bottom_right,rgba(74,124,47,0.45),transparent_70%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-primary text-xs font-bold tracking-[0.35em] uppercase mb-5"
          >
            Residential Siding · NY / CT / MA
          </motion.p>
          <KineticLines
            lines={["SIDING THAT", "OUTLIVES", "THE STORM."]}
            className="font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.88] tracking-wide"
          />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={scrollToQuote}
              data-testid="hero-quote-cta"
              className="rounded-lg bg-primary text-primary-foreground px-8 py-4 font-bold uppercase tracking-widest text-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_12px_36px_rgba(193,214,46,0.35)]"
            >
              Get a Free Quote
            </button>
            <span className="text-white/60 text-sm flex items-center gap-2"><ArrowDown size={15} className="text-primary animate-bounce" aria-hidden="true" /> Scroll to see our work</span>
          </motion.div>
        </div>
      </section>

      <div className="border-b border-white/10 bg-card">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="flex items-center gap-3">
              <s.icon size={20} className="text-primary shrink-0" aria-hidden="true" />
              <span className="text-xs md:text-sm font-bold uppercase tracking-widest text-white/80" data-testid={`stat-${i}`}>{s.label}</span>
            </Reveal>
          ))}
        </div>
      </div>

      <TextMarquee items={["INFINITE SIDING", "VINYL", "FIBER CEMENT", "CEDAR SHAKE", "METAL PANELS"]} />
      <Chapters items={CHAPTERS} />

      <section data-testid="more-services" className="border-y border-white/10 bg-card px-5 md:px-10 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">More Than Siding</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.9] mb-8">ONE CREW FOR<br />THE WHOLE EXTERIOR</h2>
            <div className="flex flex-wrap gap-3">
              {["New Additions", "Repairs", "Water Damage Control", "Decks & Stairs", "Roofing", "Paint"].map((s, i) => (
                <span key={s} data-testid={`extra-service-${i}`} className="rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-xs md:text-sm font-bold uppercase tracking-widest text-white/80 transition-colors duration-300 hover:border-primary hover:text-primary">
                  {s}
                </span>
              ))}
            </div>
            <p className="mt-8 text-white/50 text-sm max-w-xl leading-relaxed">
              From framing a new addition to repainting after a water-damage repair — one call, one crew, one standard of work.
            </p>
          </Reveal>
        </div>
      </section>

      <Gallery category="residential" />
      <QuoteForm variant="residential" />
      <Footer />
    </div>
  );
}
